/**
 * V2 → V1 shape mapper.
 *
 * O backend V2 retorna nomes de coluna novos (image_url, price_cents, is_active),
 * mas componentes da UI legada ainda esperam os nomes V1 (image, price, status).
 * Em vez de mudar 30+ componentes, traduzimos no boundary (services/contexts) usando
 * estes helpers.
 *
 * Regra: cada mapper aceita a resposta V2 e devolve um objeto com TODAS as chaves V1
 * que a UI espera. Mantém as chaves V2 originais também (não-destrutivo) para que
 * código que já leu o nome novo continue funcionando.
 */

/**
 * Mapeia um produto V2 pro shape esperado pela UI V1.
 *
 * V2 columns:                      V1 expected:
 *   image_url                  ->  image
 *   status: 'active'|'inactive' (V1 string já bate; mantém)
 *   plans[].price_cents        ->  plans[].price
 *
 * @param {Object} v2 - produto retornado por /api/v2/products
 * @returns {Object} produto no shape V1 + chaves V2 originais preservadas
 */
export function mapProductV2ToV1(v2) {
  if (!v2) return v2;

  return {
    ...v2,
    image: v2.image ?? v2.image_url ?? null,
    plans: Array.isArray(v2.plans)
      ? v2.plans.map(mapPlanV2ToV1)
      : v2.plans,
  };
}

/**
 * Mapeia um plano V2 pro shape V1.
 *   price_cents -> price
 *   is_active   -> status ('active'|'inactive')
 */
export function mapPlanV2ToV1(plan) {
  if (!plan) return plan;
  return {
    ...plan,
    price: plan.price ?? plan.price_cents ?? 0,
    status: plan.status ?? (plan.is_active ? 'active' : 'inactive'),
  };
}

/**
 * Mapeia lista de produtos V2 → V1.
 */
export function mapProductListV2ToV1(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapProductV2ToV1);
}

/**
 * Mapeia um vínculo de afiliação V2 (product_affiliates V1 dropada).
 *
 * V2: { id, product_id, user_id, status, commission_value_override, affiliate_code, created_at, ... }
 * V1 expected (em ProductsAffiliates.jsx, MyAffiliateProductsPage): { product, status, commission, affiliate_code, link }
 *
 * Convenção: backend V2 já entrega `product` aninhado em GET /api/v2/affiliates/me.
 * Aqui só normalizamos campos pra UI.
 */
export function mapAffiliateLinkV2ToV1(link) {
  if (!link) return link;
  return {
    ...link,
    commission: link.commission ?? link.commission_value_override ?? null,
    product: link.product ? mapProductV2ToV1(link.product) : link.product,
  };
}

export function mapAffiliateListV2ToV1(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapAffiliateLinkV2ToV1);
}

export default {
  mapProductV2ToV1,
  mapPlanV2ToV1,
  mapProductListV2ToV1,
  mapAffiliateLinkV2ToV1,
  mapAffiliateListV2ToV1,
};
